import { useEffect } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'

// Generic API hook types
export interface ApiResponse<T> {
  data: T
  total?: number
  page?: number
  size?: number
  pages?: number
  has_next?: boolean
  has_prev?: boolean
}

export interface ApiError {
  detail: string
  status_code: number
  request_id?: string
}

function toastApiError(err: unknown, fallback = 'An error occurred') {
  if (!(err instanceof Error)) return
  const axiosError = err as unknown as AxiosError<ApiError>
  const status = axiosError.response?.status
  // 401 is handled by the axios interceptor (refresh + redirect); skip.
  if (status === 401) return
  const message = axiosError.response?.data?.detail || fallback
  toast.error(message)
}

// Generic useQuery wrapper. React Query v5 removed the `onError` callback from
// `useQuery`, so we surface errors via the returned `error` field and a side
// effect — same UX, supported API.
export function useApiQuery<TData = unknown, TError = AxiosError<ApiError>>(
  options: UseQueryOptions<TData, TError>,
) {
  const result = useQuery(options)
  useEffect(() => {
    if (result.error) toastApiError(result.error)
  }, [result.error])
  return result
}

// Generic useMutation wrapper with error handling and success notifications.
// Mutations still support onError/onSuccess callbacks in v5, but the signature
// gained a fourth argument — we delegate cleanly with the rest spread.
export function useApiMutation<
  TData = unknown,
  TError = AxiosError<ApiError>,
  TVariables = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables> & {
    successMessage?: string
    errorMessage?: string
  },
) {
  const { successMessage, errorMessage, onSuccess, onError, ...rest } = options

  return useMutation({
    ...rest,
    onSuccess: (...args) => {
      if (successMessage) toast.success(successMessage)
      onSuccess?.(...args)
    },
    onError: (...args) => {
      toastApiError(args[0], errorMessage)
      onError?.(...args)
    },
  })
}

// Optimistic update helper.
export function useOptimisticMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  {
    queryKey,
    updateFn,
    successMessage,
  }: {
    queryKey: string[]
    updateFn: (oldData: any, variables: TVariables) => any
    successMessage?: string
  },
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onMutate: async (variables: TVariables) => {
      // Cancel outgoing refetches.
      await queryClient.cancelQueries({ queryKey })

      // Snapshot previous value.
      const previousData = queryClient.getQueryData(queryKey)

      // Optimistically update.
      queryClient.setQueryData(queryKey, (old: any) => updateFn(old, variables))

      return { previousData }
    },
    onError: (_error, _variables, context) => {
      // Rollback on error.
      if (context && (context as any).previousData) {
        queryClient.setQueryData(queryKey, (context as any).previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success.
      queryClient.invalidateQueries({ queryKey })
    },
    onSuccess: () => {
      if (successMessage) {
        toast.success(successMessage)
      }
    },
  })
}
